import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import PhoneOtpVerify from '../components/PhoneOtpVerify';

const SCIENCE_SUBJECTS = ['Mathematics','Physics','Chemistry','Biology','Computer Science','Statistics','Botany','Zoology'];
const ARTS_SUBJECTS = ['History','Geography','Political Science','Economics','Sociology','Psychology','English','Hindi','Bengali','Sanskrit','Philosophy'];

export default function Register() {
  const [form, setForm] = useState({ phone:'', full_name:'', password:'', confirm_password:'', role:'', category:'', subjects:[], custom_subject:'' });
  const [loading, setLoading] = useState(false);
  const [idToken, setIdToken] = useState(null);
  const navigate = useNavigate();
  const { settings } = useAuth();

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // A phone edit invalidates any prior OTP verification — the verified idToken
  // must never be sent alongside a different phone number than the one it verified.
  const setPhone = v => { set('phone', v); setIdToken(null); };

  const toggleSubject = s => set('subjects', form.subjects.includes(s) ? form.subjects.filter(x => x !== s) : [...form.subjects, s]);

  const addCustomSubject = () => {
    if (!form.custom_subject.trim()) return;
    const customs = form.custom_subject.split(',').map(s => s.trim()).filter(Boolean);
    const newSubjects = [...new Set([...form.subjects, ...customs])];
    set('subjects', newSubjects);
    set('custom_subject', '');
    toast.success(`Added: ${customs.join(', ')}`);
  };

  const getSubjects = () => {
    if (form.role === 'science_teacher') return SCIENCE_SUBJECTS;
    if (form.role === 'arts_teacher') return ARTS_SUBJECTS;
    if (form.role === 'school') {
      if (form.category === 'science') return SCIENCE_SUBJECTS;
      if (form.category === 'arts') return ARTS_SUBJECTS;
      return [...SCIENCE_SUBJECTS, ...ARTS_SUBJECTS];
    }
    return [];
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.password !== form.confirm_password) return toast.error('Passwords do not match');
    if (!form.role) return toast.error('Please select account type');
    if (!idToken) return toast.error('Please verify your phone number first');
    setLoading(true);
    try {
      await API.post('/auth/register', { phone: form.phone, full_name: form.full_name, password: form.password, role: form.role, category: form.category, subjects: form.subjects, idToken });
      toast.success('Account created! 30-day free trial started 🎉');
      navigate('/login');
    } catch (err) { toast.error(err.response?.data?.error || 'Registration failed'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', padding:20, background:'radial-gradient(ellipse at 80% 50%, rgba(99,102,241,0.15) 0%, transparent 60%), var(--bg)' }}>
      <div style={{ width:'100%', maxWidth:500 }}>
        <div style={{ textAlign:'center', marginBottom:24 }}>
          <h1 style={{ fontSize:28, fontWeight:800, color:'var(--primary-light)' }}>{settings.brand_name || 'Oexams'}</h1>
          <p style={{ color:'var(--text2)', fontSize:14 }}>Create account — 30 days free!</p>
        </div>
        <div className="card">
          <h2 style={{ fontSize:20, fontWeight:700, marginBottom:20 }}>Create Account</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Account Type *</label>
              <select value={form.role} onChange={e => set('role', e.target.value)} required>
                <option value="">Select type</option>
                <option value="science_teacher">🔬 Science Teacher</option>
                <option value="arts_teacher">🎨 Arts Teacher</option>
                <option value="school">🏫 School / Coaching Center</option>
              </select>
            </div>
            <div className="form-group">
              <label>{form.role === 'school' ? 'School / Coaching Name *' : 'Full Name *'}</label>
              <input value={form.full_name} onChange={e => set('full_name', e.target.value)} placeholder="Enter name" required />
            </div>
            <div className="form-group">
              <label>Phone Number *</label>
              <input type="tel" value={form.phone} onChange={e => setPhone(e.target.value)} placeholder="10-digit phone" required />
            </div>
            {/^[0-9]{10}$/.test(form.phone) && (
              <PhoneOtpVerify phone={form.phone} verified={!!idToken} onVerified={setIdToken} disabled={loading} />
            )}
            {form.role === 'school' && (
              <div className="form-group">
                <label>Category *</label>
                <select value={form.category} onChange={e => set('category', e.target.value)} required>
                  <option value="">Select</option>
                  <option value="science">Science</option>
                  <option value="arts">Arts</option>
                  <option value="both">Both</option>
                </select>
              </div>
            )}
            {form.role && (form.role !== 'school' || form.category) && (
              <div className="form-group">
                <label>Subjects * (select multiple)</label>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:10 }}>
                  {getSubjects().map(s => (
                    <button type="button" key={s} onClick={() => toggleSubject(s)} style={{
                      padding:'5px 12px', borderRadius:20, border:'1px solid', cursor:'pointer', fontSize:12, fontWeight:600,
                      background: form.subjects.includes(s) ? 'var(--primary)' : 'var(--card2)',
                      color: form.subjects.includes(s) ? 'white' : 'var(--text2)',
                      borderColor: form.subjects.includes(s) ? 'var(--primary)' : 'var(--border)'
                    }}>{s}</button>
                  ))}
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <input value={form.custom_subject} onChange={e => set('custom_subject', e.target.value)}
                    placeholder="Add custom subject (e.g. Accountancy, EVS)" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addCustomSubject())} />
                  <button type="button" onClick={addCustomSubject} className="btn btn-secondary btn-sm" style={{ whiteSpace:'nowrap' }}>+ Add</button>
                </div>
                <p style={{ fontSize:11, color:'var(--text3)', marginTop:4 }}>Tip: separate multiple with comma e.g. "Accountancy, EVS, IT"</p>
                {form.subjects.length > 0 && (
                  <div style={{ marginTop:8, display:'flex', flexWrap:'wrap', gap:4 }}>
                    {form.subjects.map(s => (
                      <span key={s} style={{ padding:'3px 10px', borderRadius:20, background:'rgba(99,102,241,0.2)', color:'var(--primary-light)', fontSize:11, display:'flex', alignItems:'center', gap:4 }}>
                        {s} <button type="button" onClick={() => set('subjects', form.subjects.filter(x=>x!==s))} style={{ background:'none', border:'none', color:'inherit', cursor:'pointer', fontSize:14, lineHeight:1 }}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="form-group">
              <label>Password *</label>
              <input type="password" value={form.password} onChange={e => set('password', e.target.value)} placeholder="Create password" required />
            </div>
            <div className="form-group">
              <label>Confirm Password *</label>
              <input type="password" value={form.confirm_password} onChange={e => set('confirm_password', e.target.value)} placeholder="Confirm password" required />
            </div>
            <button type="submit" className="btn btn-primary btn-full btn-lg" disabled={loading}>
              {loading ? '⏳ Creating...' : '✅ Create Account (Free Trial)'}
            </button>
          </form>
          <p style={{ textAlign:'center', marginTop:16, color:'var(--text2)', fontSize:13 }}>
            Already have account? <Link to="/login" style={{ color:'var(--primary-light)', fontWeight:600 }}>Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
